import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Copy, ChevronRight, Loader2 } from "lucide-react";
import { StepIndicator } from "./StepIndicator";

export function FacebookGuidedSetup({ integration, formData, setFormData, webhookInfo, handleUpdate, handleTest, testing, copyToClipboard }) {
  const [currentStep, setCurrentStep] = useState(1);
  const creds = formData.credentials || {};
  const hasPageToken = !!creds.access_token;
  const hasPageId = !!creds.page_id;

  // Auto-advance step based on filled fields
  const effectiveStep = !hasPageToken ? 1 : !hasPageId ? 2 : !webhookInfo ? 3 : currentStep < 4 ? Math.max(currentStep, 3) : currentStep;

  const steps = [
    { step: 1, label: "Create Facebook App & Get Page Token" },
    { step: 2, label: "Enter Page ID" },
    { step: 3, label: "Configure Webhook in Facebook" },
    { step: 4, label: "Subscribe to Lead Ads & Test" },
  ];

  return (
    <div className="space-y-4" data-testid="fb-guided-setup">
      {/* Progress Steps */}
      <div className="space-y-2">
        {steps.map(s => (
          <button key={s.step} onClick={() => setCurrentStep(s.step)} className="w-full text-left">
            <StepIndicator step={s.step} currentStep={effectiveStep < s.step ? effectiveStep : currentStep >= s.step ? currentStep : effectiveStep} label={s.label} />
          </button>
        ))}
      </div>

      {/* Step Content */}
      {currentStep === 1 && (
        <div className="space-y-4 pt-2">
          <div className="p-4 bg-[#1877F2]/5 border border-[#1877F2]/15 rounded-xl">
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Step 1: Get a Page Access Token</h4>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">1.</span>
                <span>Go to <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-[#1877F2] underline font-medium">Meta for Developers</a> and create a new App (type: <strong>Business</strong>)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">2.</span>
                <span>In your App, go to <strong>Add Products</strong> → find <strong>"Webhooks"</strong> → click <strong>Set Up</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">3.</span>
                <span>Also add the <strong>"Facebook Login"</strong> product</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">4.</span>
                <span>Go to <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-[#1877F2] underline font-medium">Graph API Explorer</a></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">5.</span>
                <span>Select your App → click <strong>"Get User Access Token"</strong> → check these permissions: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">pages_manage_ads</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">leads_retrieval</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">pages_show_list</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">pages_read_engagement</code></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">6.</span>
                <span>Click <strong>"Generate Access Token"</strong> and authorize your Page. Then convert to a <strong>long-lived Page Access Token</strong> using the token debugger or API call.</span>
              </li>
            </ol>
          </div>
          <div className="space-y-2">
            <Label className="font-medium">Page Access Token</Label>
            <Input
              type="password"
              value={creds.access_token || ""}
              onChange={(e) => setFormData({ ...formData, credentials: { ...creds, access_token: e.target.value } })}
              placeholder="EAAxxxxxxx..."
              className="border-gray-300 rounded-lg font-mono text-sm"
              data-testid="fb-access-token"
            />
            <p className="text-xs text-gray-500">Paste your long-lived Page Access Token here</p>
          </div>
          <Button onClick={() => { handleUpdate(); setCurrentStep(2); }} disabled={!creds.access_token} className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-lg" data-testid="fb-step1-next">
            Save & Continue <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {currentStep === 2 && (
        <div className="space-y-4 pt-2">
          <div className="p-4 bg-[#1877F2]/5 border border-[#1877F2]/15 rounded-xl">
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Step 2: Find Your Page ID</h4>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">1.</span>
                <span>Go to your <a href="https://www.facebook.com/pages/?category=your_pages" target="_blank" rel="noopener noreferrer" className="text-[#1877F2] underline font-medium">Facebook Page</a></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">2.</span>
                <span>Click <strong>About</strong> → scroll to the bottom → find <strong>Page ID</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">3.</span>
                <span>Or use the Graph API Explorer: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">GET /me?fields=id,name</code> with your token</span>
              </li>
            </ol>
          </div>
          <div className="space-y-2">
            <Label className="font-medium">Facebook Page ID</Label>
            <Input
              value={creds.page_id || ""}
              onChange={(e) => setFormData({ ...formData, credentials: { ...creds, page_id: e.target.value } })}
              placeholder="123456789012345"
              className="border-gray-300 rounded-lg font-mono text-sm"
              data-testid="fb-page-id"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentStep(1)} className="border-gray-300 rounded-lg">Back</Button>
            <Button onClick={() => { handleUpdate(); setCurrentStep(3); }} disabled={!creds.page_id} className="flex-1 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-lg" data-testid="fb-step2-next">
              Save & Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className="space-y-4 pt-2">
          <div className="p-4 bg-[#1877F2]/5 border border-[#1877F2]/15 rounded-xl">
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Step 3: Set Up Webhook in Facebook</h4>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">1.</span>
                <span>In your Facebook App, go to <strong>Webhooks</strong> product</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">2.</span>
                <span>Select <strong>"Page"</strong> from the dropdown</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">3.</span>
                <span>Click <strong>"Subscribe to this topic"</strong> (or Edit if already set)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">4.</span>
                <span>Paste the <strong>Callback URL</strong> and <strong>Verify Token</strong> below:</span>
              </li>
            </ol>
          </div>

          {webhookInfo && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div>
                <Label className="text-xs font-medium text-gray-500 mb-1 block">Callback URL</Label>
                <div className="flex gap-2">
                  <code className="flex-1 text-xs bg-white px-3 py-2.5 rounded-lg border border-gray-200 font-mono break-all select-all">{webhookInfo.webhook_url}</code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.webhook_url)} className="shrink-0 h-9"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-500 mb-1 block">Verify Token</Label>
                <div className="flex gap-2">
                  <code className="flex-1 text-xs bg-white px-3 py-2.5 rounded-lg border border-gray-200 font-mono select-all">{webhookInfo.verify_token}</code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.verify_token)} className="shrink-0 h-9"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          )}

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800"><strong>Important:</strong> After pasting, click <strong>"Verify and Save"</strong> in Facebook. Then find <strong>"leadgen"</strong> in the subscription fields and click <strong>Subscribe</strong>.</p>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentStep(2)} className="border-gray-300 rounded-lg">Back</Button>
            <Button onClick={() => setCurrentStep(4)} className="flex-1 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-lg" data-testid="fb-step3-next">
              I've Done This <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {currentStep === 4 && (
        <div className="space-y-4 pt-2">
          <div className="p-4 bg-[#1877F2]/5 border border-[#1877F2]/15 rounded-xl">
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Step 4: Subscribe Your Page & Test</h4>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">1.</span>
                <span>In your Facebook App, go to <strong>Webhooks</strong> → Page → check <strong>"leadgen"</strong> is subscribed</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">2.</span>
                <span>Go to <strong>Facebook Ads Manager</strong> → create a Lead Ad campaign (or use an existing one)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">3.</span>
                <span>When a user clicks your Ad CTA and fills the Lead Form, it will <strong>automatically create a lead</strong> in POMEN!</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#1877F2] shrink-0">4.</span>
                <span>Use the <strong>Test Connection</strong> button to verify your credentials work. Use <strong>Sync</strong> to pull existing leads from your forms.</span>
              </li>
            </ol>
          </div>

          {/* Summary Card */}
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
            <h5 className="text-sm font-semibold text-gray-900">Configuration Summary</h5>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-gray-500">Page Token:</span> <span className={creds.access_token ? "text-emerald-600 font-medium" : "text-red-500"}>{ creds.access_token ? "Configured" : "Missing"}</span></div>
              <div><span className="text-gray-500">Page ID:</span> <span className={creds.page_id ? "text-emerald-600 font-medium" : "text-red-500"}>{creds.page_id || "Missing"}</span></div>
              <div><span className="text-gray-500">Webhook:</span> <span className={webhookInfo ? "text-emerald-600 font-medium" : "text-red-500"}>{webhookInfo ? "Ready" : "Not generated"}</span></div>
              <div><span className="text-gray-500">Status:</span> {integration.status === "connected" ? <span className="text-emerald-600 font-medium">Connected</span> : <span className="text-amber-600 font-medium">Pending test</span>}</div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentStep(3)} className="border-gray-300 rounded-lg">Back</Button>
            <Button variant="outline" onClick={handleTest} disabled={testing} className="flex-1 border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2]/5" data-testid="fb-test-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Test Connection
            </Button>
          </div>

          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-xs text-emerald-800 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span><strong>How it works:</strong> When someone fills your Facebook Lead Ad form, Facebook sends the data to POMEN via webhook. A new lead is automatically created with all the form fields (name, email, phone, etc.).</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
